import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarClock, Check, Copy, Mail, MessageCircle, Plus, Send, X } from 'lucide-react';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/cn';
import { createMeeting, fetchCallCandidates, whatsappText, type CallInvitee, type Meeting } from '@/lib/calls';

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

/** Book one call for one or several of a clinic's scripts. Staff choose the scripts
 *  and who is invited; the clinic only picks the time, from the link. */
export default function BookCallDialog({ creativeId, open, onClose }: { creativeId: string; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const q = useQuery({ queryKey: ['call-candidates', creativeId], queryFn: () => fetchCallCandidates(creativeId), enabled: open });
  const data = q.data;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [invitees, setInvitees] = useState<CallInvitee[]>([]);
  const [organizer, setOrganizer] = useState('');
  const [adding, setAdding] = useState({ name: '', email: '' });
  const [sent, setSent] = useState<Meeting | null>(null);

  useEffect(() => {
    if (!open) { setSent(null); return; }
    if (!data) return;
    const start = new Set<string>();
    for (const g of data.groups) for (const s of g.scripts) if (s.eligible && s.creative_id === creativeId) start.add(s.creative_id);
    setPicked(start);
    setInvitees(data.suggested_invitees);
    setOrganizer(user?.id && data.organizers.some((o) => o.id === user.id) ? user.id : (data.organizers[0]?.id ?? ''));
  }, [open, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickedDoctors = useMemo(() => {
    if (!data) return new Set<string>();
    return new Set(data.groups.filter((g) => g.scripts.some((s) => picked.has(s.creative_id))).map((g) => g.talent_id || ''));
  }, [data, picked]);

  const send = useMutation({
    mutationFn: () => createMeeting({ creative_ids: [...picked], invitees, organizer_id: organizer || undefined }),
    onSuccess: (m) => {
      setSent(m);
      qc.invalidateQueries({ queryKey: ['meetings'] });
      qc.invalidateQueries({ queryKey: ['call-candidates', creativeId] });
      toast.success(m.dry_run ? 'Call created (dry run: nothing was sent)' : 'Booking link sent');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!open) return null;

  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const addInvitee = () => {
    const email = adding.email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error('That email does not look right'); return; }
    if (invitees.some((i) => i.email.toLowerCase() === email)) return;
    setInvitees([...invitees, { email, name: adding.name.trim() || email, kind: 'clinic' }]);
    setAdding({ name: '', email: '' });
  };
  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error('Copy did not work; select the text instead'); }
  };

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[300] flex items-start sm:items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={() => !send.isPending && onClose()}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl my-4 rounded-xl border border-slate-800 bg-bg-card shadow-2xl" data-testid="book-call-dialog">
        <div className="flex items-start gap-3 p-5 border-b border-slate-800">
          <CalendarClock size={18} className="text-accent-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-muted-strong">{sent ? 'Booking link sent' : 'Book a script call'}</h3>
            <p className="text-xs text-muted mt-1">{sent
              ? 'The clinic picks a time from the link. You are told when they book.'
              : `One call for as many of ${data?.client_name ?? 'this clinic'}'s scripts as you want to go through.`}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-muted hover:text-muted-strong" aria-label="Close"><X size={16} /></button>
        </div>

        {q.isLoading && <div className="p-5 text-sm text-muted">Loading scripts...</div>}

        {data && !sent && (
          <div className="p-5 space-y-5">
            {data.dry_run && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300" data-testid="dry-run-note">
                Dry run: Google is not connected yet, so no email, invitation or calendar event will be sent.
              </div>
            )}

            <section>
              <div className="text-[10px] uppercase tracking-wide text-muted mb-2">Scripts on the call</div>
              <div className="space-y-3">
                {data.groups.map((g) => (
                  <div key={g.talent_id || 'none'}>
                    <div className="text-xs font-medium text-muted-strong mb-1">{g.talent_name}</div>
                    <div className="space-y-1">
                      {g.scripts.map((s) => (
                        <label key={s.creative_id} className={cn('flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm',
                          s.eligible ? 'hover:bg-slate-800/40 cursor-pointer' : 'opacity-60')} data-testid="call-script">
                          <input type="checkbox" className="accent-accent-400 mt-1" disabled={!s.eligible}
                            checked={picked.has(s.creative_id)} onChange={() => toggle(s.creative_id)} />
                          <span className="flex-1 min-w-0">
                            <span className="font-mono text-[11px] text-muted mr-2">{s.code}</span>
                            <span className="text-muted-strong">{s.title}</span>
                            {!s.eligible && <span className="block text-[11px] text-muted">{s.reason}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="text-[10px] uppercase tracking-wide text-muted mb-2">Who gets the link</div>
              <div className="space-y-1.5">
                {invitees.map((i) => (
                  <div key={i.email} className="flex items-center gap-2 text-sm rounded-lg bg-bg px-3 py-2">
                    <Mail size={12} className="text-muted shrink-0" />
                    <span className="text-muted-strong">{i.name}</span>
                    <span className="text-muted text-xs truncate">{i.email}</span>
                    <span className="ml-auto text-[10px] uppercase text-muted">{i.kind}</span>
                    {!(i.kind === 'doctor' && pickedDoctors.has(i.talent_id || '')) && (
                      <button type="button" className="text-muted hover:text-red-300" aria-label={`Remove ${i.name}`}
                        onClick={() => setInvitees(invitees.filter((x) => x.email !== i.email))}><X size={12} /></button>
                    )}
                  </div>
                ))}
                {invitees.length === 0 && <p className="text-xs text-muted">Nobody yet. Add the clinic contact below.</p>}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <input value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} placeholder="Name"
                  className="h-9 px-3 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong sm:w-40" />
                <input value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} placeholder="email@clinic.com" type="email"
                  className="h-9 px-3 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInvitee(); } }} />
                <Button size="sm" variant="secondary" icon={<Plus size={12} />} onClick={addInvitee}>Add</Button>
              </div>
              <p className="text-[11px] text-muted mt-1.5">The doctor on each script is always invited. Only people on this list can book.</p>
            </section>

            <section className="max-w-xs">
              <Select label="Runs the call" value={organizer} onChange={setOrganizer}
                options={data.organizers.map((o) => ({ value: o.id, label: o.name }))} searchable />
              <p className="text-[11px] text-muted mt-1">The clinic sees this person's free times. You can hand it over later.</p>
            </section>
          </div>
        )}

        {sent && (
          <div className="p-5 space-y-4" data-testid="call-sent">
            <div className="space-y-1">
              {sent.deliveries.map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-xs">
                  {d.channel === 'telegram' ? <MessageCircle size={12} className="text-muted" /> : <Mail size={12} className="text-muted" />}
                  <span className="text-muted-strong truncate">{d.channel === 'telegram' ? 'Telegram' : d.recipient}</span>
                  <span className={cn('ml-auto', d.status === 'sent' ? 'text-emerald-300' : d.status === 'failed' ? 'text-red-300' : 'text-amber-300')}>
                    {d.error === 'dry-run: not sent' ? 'dry run, not sent' : d.status}
                  </span>
                </div>
              ))}
            </div>
            {sent.link && (
              <div className="rounded-lg border border-slate-800 bg-bg p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-wide text-muted">Send it on WhatsApp too</div>
                <code className="block text-[11px] text-muted-strong break-all select-all" data-testid="call-link">{sent.link}</code>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" icon={<Copy size={12} />} onClick={() => copy(sent.link!, 'Link')}>Copy link</Button>
                  <Button size="sm" variant="secondary" icon={<MessageCircle size={12} />}
                    onClick={() => copy(whatsappText(sent.invitees.find((i) => i.kind !== 'staff')?.name ?? '', sent.creatives.length, sent.link!), 'WhatsApp message')}>
                    Copy WhatsApp message
                  </Button>
                </div>
              </div>
            )}
            <Link to={`/meetings/${sent.id}`} className="text-sm text-accent-400 hover:underline">Open the call</Link>
          </div>
        )}

        <div className="flex justify-end gap-2 p-4 border-t border-slate-800">
          {sent ? (
            <Button icon={<Check size={14} />} onClick={onClose}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={send.isPending}>Cancel</Button>
              <Button icon={<Send size={14} />} loading={send.isPending} disabled={!picked.size || !invitees.length || !organizer}
                onClick={() => send.mutate()} data-testid="call-send">
                Send the link{picked.size > 1 ? ` for ${picked.size} scripts` : ''}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
